import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { observer } from "mobx-react";
import { Badge, Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useNavigate } from "react-router-dom";
import {orderApi} from "../../../api/order.js";

function CustomerDashboard() {
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const columnDefs = [
        { headerName: "Order #", field: "orderId", sortable: true, filter: true, resizable: true, maxWidth: 140 },
        { headerName: "Tax Authority", field: "taxAuthority", sortable: true, filter: true, resizable: true },
        { headerName: "Customer Ref #", field: "customerReference", sortable: true, filter: true, resizable: true },
        { headerName: "Search Criteria", field: "searchTerm", sortable: true, filter: true, resizable: true },
        { headerName: "Type", field: "searchType", sortable: true, filter: true, resizable: true, valueFormatter: (p) => (p.value ? p.value.toUpperCase() : "-"), maxWidth: 120 },
        { headerName: "Order Date", field: "orderDate", sortable: true, filter: true, resizable: true, valueFormatter: (p) => (p.value ? p.value.toLocaleString() : "-") },
        {
            headerName: "Actions",
            field: "id",
            cellRenderer: "actionRenderer",
            resizable: true
        }
    ];

    //navigate to new order page
    const navigate = useNavigate();
    const handleNewOrder = () => {
        navigate('/order/new');
    }

    const gridRef = useRef(null);

    const components = useMemo(() => ({
        actionRenderer: ActionRenderer,
    }), []);

    const sizeToFit = useCallback(() => {
        if (gridRef.current && gridRef.current.api) {
            gridRef.current.api.sizeColumnsToFit();
        }
    }, []);

    useEffect(() => {
        const loadOrders = async () => {
            setIsLoading(true);
            try {
                const orders = await orderApi.getOrders();
                const normalized = orders.map((order) => ({
                    id: order.orderId,
                    orderId: order.orderId,
                    taxAuthority: order.taxAuthority,
                    customerReference: order.customerReference,
                    searchTerm: order.searchTerm,
                    searchType: order.searchType ? order.searchType.toLowerCase() : "",
                    orderDate: order.orderDate ? new Date(order.orderDate) : null
                }));
                setRowData(normalized);
                setError("");
            } catch (e) {
                console.error("Failed to load customer orders", e);
                setError("Failed to load recent orders.");
            } finally {
                setIsLoading(false);
            }
        };
        loadOrders();
    }, []);

    useEffect(() => {
        sizeToFit();
        window.addEventListener("resize", sizeToFit);
        return () => window.removeEventListener("resize", sizeToFit);
    }, [sizeToFit]);

    const stats = useMemo(() => {
        const sorted = [...rowData].sort((a, b) => (b.orderDate || 0) - (a.orderDate || 0));
        const lastOrder = sorted.length ? (sorted[0].orderDate ? sorted[0].orderDate.toLocaleString() : "N/A") : "N/A";
        const recentWindowMs = 1000 * 60 * 60 * 24 * 3; // last 3 days
        const now = Date.now();
        const recentOrders = sorted.filter(o => o.orderDate && (now - o.orderDate.getTime()) <= recentWindowMs).length;
        return [
            { label: "Total Orders", value: rowData.length },
            { label: "Most Recent Order", value: lastOrder },
            { label: "Recent (3d)", value: recentOrders }
        ];
    }, [rowData]);

    return (
        <Container className="dashboard-shell">
            <div className="dashboard-hero" data-accent="customer">
                <div>
                    <div className="dashboard-eyebrow">Customer Dashboard</div>
                    <h1>Welcome back</h1>
                    <p>Track your tax certificate activity and start new requests in seconds.</p>
                </div>
                <div className="dashboard-hero-actions">
                    <Button variant="primary" onClick={handleNewOrder}>
                        New Order
                    </Button>
                    <Button variant="outline-secondary" href="/customer/orders">
                        View Orders
                    </Button>
                </div>
            </div>

            <Row className="mt-4 g-3">
                {stats.map((stat) => (
                    <Col md={4} key={stat.label}>
                        <Card className="stat-card">
                            <Card.Body>
                                <div className="stat-label">{stat.label}</div>
                                <div className="stat-value">{stat.value}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row className="mt-4 g-4">
                <Col lg={12}>
                    <Card className="dashboard-panel">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Recent Orders</strong>
                            <Badge bg="light" text="dark">Updated just now</Badge>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                            {isLoading ? (
                                <div className="d-flex align-items-center">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    <span>Loading orders...</span>
                                </div>
                            ) : (
                                <div className="ag-theme-alpine dashboard-grid" style={{ height: "100%", width: "100%" }}>
                                    <AgGridReact
                                        ref={gridRef}
                                        rowData={rowData}
                                        columnDefs={columnDefs}
                                        components={components}
                                        pagination={true}
                                        paginationPageSize={7}
                                        domLayout="autoHeight"
                                        onGridReady={sizeToFit}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={12}>
                    <Card className="dashboard-panel mb-6">
                        <Card.Body>
                            <h5 className="mb-2">Order a Tax Certificate</h5>
                            <p className="text-muted mb-3">Start a new order when you are ready to request a certificate.</p>
                            <Button variant="primary" onClick={handleNewOrder}>Start Order</Button>
                        </Card.Body>
                    </Card>
                    
                </Col>
            </Row>
        </Container>
    );
}

function ActionRenderer(props) {
    return (
        <div style={{ textAlign: "center" }}>
            <a href={`/order/${props.data.id}`}><i className="bi bi-file-pdf" style={{ fontSize: 18 }}></i></a>&nbsp;
            <a href={`/order/${props.data.id}`}><i className="bi bi-search" style={{ fontSize: 18 }}></i></a>
        </div>
    );
}

export default observer(CustomerDashboard);
